/**
 * useRoomBoard — list of rooms for the readiness board.
 *
 * Reads via the authenticated client; RLS scopes to the user's org. The
 * data shape carries each room's status string for display only —
 * transitions still go exclusively through the room-transition Edge
 * Function (CLAUDE.md ERROR 5 / rule 4). This hook never writes.
 *
 * Active-only (`is_active = true`); soft-deleted rooms never appear.
 *
 * Filters are applied client-side after the initial fetch — the org
 * typically has a few hundred rooms which fits comfortably in one page.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useSupabase }                    from './useSupabase'

export type RoomStatus =
  | 'occupied'
  | 'move_out_pending'
  | 'vacant_dirty'
  | 'cleaning_assigned'
  | 'cleaning_in_progress'
  | 'inspection_pending'
  | 'maintenance_required'
  | 'ready'
  | 'reserved'

export interface RoomBoardRow {
  id:             string
  room_number:    string
  current_status: RoomStatus
  campus_id:      string | null
  building_id:    string
  floor_id:       string
  campus:   { id: string; name: string } | null
  building: { id: string; name: string } | null
  floor:    { id: string; label: string } | null
}

const ROOMS_TABLE  = 'rooms'
const ROOM_COLUMNS =
  'id, room_number, current_status, campus_id, building_id, floor_id,' +
  ' campus:campuses(id, name),' +
  ' building:buildings(id, name),' +
  ' floor:floors(id, label)'

export function useRoomBoard(): UseQueryResult<RoomBoardRow[]> {
  const supabase = useSupabase()
  return useQuery({
    queryKey: ['room-board'],
    queryFn: async (): Promise<RoomBoardRow[]> => {
      const { data, error } = await supabase
        .from(ROOMS_TABLE)
        .select(ROOM_COLUMNS)
        .eq('is_active', true)
        .order('room_number', { ascending: true })
        .limit(2000)

      if (error) throw error
      return (data ?? []) as unknown as RoomBoardRow[]
    },
    refetchInterval: 60_000,
  })
}

/**
 * Display order on the board — operational urgency, not alphabetical.
 * Pinned here so the order is one decision in one place.
 */
export const STATUS_DISPLAY_ORDER: readonly RoomStatus[] = [
  'maintenance_required',
  'inspection_pending',
  'cleaning_in_progress',
  'cleaning_assigned',
  'vacant_dirty',
  'move_out_pending',
  'ready',
  'reserved',
  'occupied',
]
